/**
 * Client-side paging for a table (max 100 rows on screen).
 *
 * Why client-side. Every list endpoint in this platform is CURSOR-paginated and
 * answers `{ items, nextCursor }` - deliberately, because an offset into a list
 * that moves while it is being read returns the same row twice or skips one. A
 * cursor page carries no total, so there is no server figure to render "1-100 of
 * 271" from. Screens therefore fetch a page (the contract caps `limit` at 200)
 * and slice it here.
 *
 * What that costs is stated rather than hidden. When the server said there is
 * more beyond what was fetched, `more` is true and the pager prints the total
 * with a `+`: the honest reading is "at least this many", and a bare "271" over
 * a truncated fetch would be the same class of defect as two figures disagreeing
 * on one screen. Pure and I/O-free, so the arithmetic is exercised directly.
 */

/** One screenful. The cap Simon asked for, and the contract's own `limit` max. */
export const TABLE_PAGE_SIZE = 100;

export interface PageSlice<Row> {
  /** The rows this page shows. */
  readonly rows: readonly Row[];
  /** 1-based, clamped into range - never a page the data cannot fill. */
  readonly page: number;
  readonly totalPages: number;
  /** 1-based inclusive bounds of `rows` within the whole set; 0 when empty. */
  readonly from: number;
  readonly to: number;
  /** Rows held here. A floor rather than a count when `more` is true. */
  readonly total: number;
  /** The server has rows beyond the ones fetched, so `total` is a floor. */
  readonly more: boolean;
  readonly canPrev: boolean;
  readonly canNext: boolean;
  /**
   * The reader is on the last page HELD and the server has more. A screen that
   * owns its query answers this by following the cursor and appending; a screen
   * that does not leaves it alone and the pager stays where it is. Separate from
   * `canNext`, which is only ever about rows already here - conflating the two
   * would enable Next on every screen that reports `more`, and clicking it would
   * clamp straight back to the page it started on.
   */
  readonly canFetchMore: boolean;
}

export interface PageSliceOptions {
  readonly pageSize?: number;
  /** Pass the truthiness of the response's `nextCursor`. */
  readonly more?: boolean;
  /**
   * How many rows EXIST, when the endpoint is willing to say.
   *
   * Almost none are: a cursor page carries no total by design, which is why
   * `more` renders as a `+` on the rows held. But `100+` over 817 orders
   * answers "how many are there" with "more than you can see", so an endpoint
   * that already counted them (`GET /internal/orders` builds every matching row
   * before slicing) can hand the figure over and the pager states it.
   *
   * It changes what is PRINTED and nothing about what is reachable: `more`
   * still decides whether there is another cursor page to fetch, because a
   * total says how many rows exist and not whether these are all of them.
   */
  readonly knownTotal?: number;
}

/**
 * Slice `all` for display.
 *
 * The requested page is CLAMPED rather than trusted. A page number outlives the
 * rows it was chosen for - filter the list, archive a row, let a refetch return
 * fewer - and a stale one would otherwise render an empty table with no rows and
 * no explanation, which reads as "nothing matches" rather than "you are past the
 * end".
 */
export function pageSlice<Row>(
  all: readonly Row[],
  requestedPage: number,
  options: PageSliceOptions = {},
): PageSlice<Row> {
  const pageSize = Math.max(1, options.pageSize ?? TABLE_PAGE_SIZE);
  const held = all.length;
  // What the reader is told there are; the PAGES below are over the rows held,
  // which is all this function can slice.
  const total = options.knownTotal ?? held;
  const totalPages = Math.max(1, Math.ceil(held / pageSize));
  const page = Math.min(Math.max(1, Math.trunc(requestedPage) || 1), totalPages);
  const start = (page - 1) * pageSize;
  const rows = all.slice(start, start + pageSize);
  return {
    rows,
    page,
    totalPages,
    from: held === 0 ? 0 : start + 1,
    to: held === 0 ? 0 : start + rows.length,
    total,
    // Nothing is hidden behind a `+` once the count is exact.
    more: options.more === true && options.knownTotal === undefined,
    canPrev: page > 1,
    /*
     * Over the rows HELD, not the rows that exist. This pager slices what a
     * screen already fetched; it does not own the query and cannot ask for the
     * next cursor. Rows beyond the fetch are reported by `more` - as the `+` on
     * the total - and, for a screen that CAN follow its cursor, by
     * `canFetchMore` below.
     */
    canNext: page < totalPages,
    canFetchMore: options.more === true && page >= totalPages,
  };
}
